import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Params } from '@angular/router';
import { environment } from '../../environments/environment';
import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { Pagination, PaginatedResponse } from './interfaces';
import { Injector } from './injector/injector';
import { JwtStorageService } from '../shared/auth/jwt-storage.service';
import { mergeMap, take, tap } from 'rxjs/operators';
import { ITranslationService, I18NEXT_SERVICE } from 'angular-i18next';
import dayjs from 'dayjs';

type METHOD = 'get' | 'post' | 'put' | 'delete';
@Injectable({
  providedIn: 'root',
})
export class BaseService {
  private httpClient: HttpClient;
  private jwtStorageSrv: JwtStorageService;
  private i18Service: ITranslationService;

  constructor() {
    this.jwtStorageSrv = new JwtStorageService();
    this.httpClient = Injector.get(HttpClient);
    this.i18Service = Injector.get(I18NEXT_SERVICE);
  }

  protected downloadExcel(path: string, params?: any): void {
    if (path.indexOf('http') !== 0) {
      path = environment.apiBaseUrl + path;
    }

    const headersOptions: any = {
      // 'x-client-name': environment.clientName,
      // 'x-client-version': environment.clientVersion,
    };

    const accessToken = this.jwtStorageSrv.getToken();
    if (accessToken) {
      headersOptions.Authorization = `Bearer ${accessToken}`;
    }

    const headers = new HttpHeaders(headersOptions);
    if (params) {
      params = this.makeArrayParam(params);
    }

    this.httpClient.get(`${path}`, { params, headers, responseType: 'arraybuffer' }).subscribe((res) => {
      this.downloadExcelFile(res, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    });
  }

  public makeArrayParamToString(value: any) {
    const s = [];

    const add = (k: string, v: any) => {
      v = typeof v === 'function' ? v() : v;
      v = v === null ? '' : v === undefined ? '' : v;
      s.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
    };

    const buildParams = (prefix: string, obj: any) => {
      if (prefix) {
        if (Array.isArray(obj)) {
          obj.forEach((v, i) => buildParams(prefix + '[' + (typeof v === 'object' && v ? i : '') + ']', v));
        } else if (obj instanceof Date) {
          add(prefix, JSON.stringify(obj));
        } else if (obj instanceof dayjs) {
          add(prefix, (obj as dayjs.Dayjs).toJSON());
        } else if (String(obj) === '[object Object]') {
          Object.keys(obj).forEach((key) => buildParams(prefix + '[' + key + ']', obj[key]));
        } else {
          add(prefix, obj);
        }
      } else if (Array.isArray(obj)) {
        obj.forEach((v) => add(v.name, v.value));
      } else {
        Object.keys(obj).forEach((key) => {
          buildParams(key, obj[key]);
        });
      }
      return s;
    };

    return buildParams(null, value).join('&');
  }

  public makeArrayParam(value: any): HttpParams {
    const params = this.makeArrayParamToString(value);
    return new HttpParams({ fromString: params });
  }

  private __check_auth(headers: HttpHeaders) {
    const renewalToken = headers.get('renewal-token');

    if (renewalToken) {
      this.jwtStorageSrv.setToken(renewalToken);
    }
  }

  private __api<T>(method: METHOD, path: string, params?: any): Observable<T> {
    if (path.indexOf('http') !== 0) {
      path = environment.apiBaseUrl + path;
    }

    const headersOptions: any = {
      // 'x-client-name': environment.clientName,
      // 'x-client-version': environment.clientVersion,
      'x-custom-lang': this.i18Service.language || 'en',
    };

    const accessToken = this.jwtStorageSrv.getToken();
    if (accessToken) {
      headersOptions.Authorization = `Bearer ${accessToken}`;
    }

    const headers = new HttpHeaders(headersOptions);
    // if (method !== 'delete' && params) {
    if (method === 'get' && params) {
      params = this.makeArrayParam(params);
    }

    if (method === 'get') {
      return this.httpClient.get(path, { params, headers, observe: 'response' }).pipe(
        mergeMap((res) => {
          this.__check_auth(res.headers);
          return of(res.body);
        }),
        take(1)
      ) as Observable<T>;
    } else if (method === 'post') {
      return this.httpClient.post(path, params, { headers, observe: 'response' }).pipe(
        mergeMap((res) => {
          this.__check_auth(res.headers);
          return of(res.body);
        }),
        take(1)
      ) as Observable<T>;
    } else if (method === 'put') {
      return this.httpClient.put(path, params, { headers, observe: 'response' }).pipe(
        mergeMap((res) => {
          this.__check_auth(res.headers);
          return of(res.body);
        }),
        take(1)
      ) as Observable<T>;
    } else if (method === 'delete') {
      return this.httpClient.request('delete', path, { body: params, headers, observe: 'response' }).pipe(
        mergeMap((res) => {
          this.__check_auth(res.headers);
          return of(res.body);
        }),
        take(1)
      ) as Observable<T>;
    }
  }

  protected get<T>(path: string, params?: Params): Observable<T> {
    return this.__api('get', path, params);
  }

  protected post<T>(path: string, params?: Params): Observable<T> {
    return this.__api('post', path, params);
  }

  protected put<T>(path: string, params?: Params): Observable<T> {
    return this.__api('put', path, params);
  }

  protected delete<T>(path: string, params?: Params): Observable<T> {
    return this.__api('delete', path, params);
  }

  protected download(path: string, params?: Params): void {
    if (path.indexOf('http') !== 0) {
      path = environment.apiBaseUrl + path;
    }

    const headersOptions: any = {
      // 'x-client-name': environment.clientName,
      // 'x-client-version': environment.clientVersion,
    };

    const accessToken = this.jwtStorageSrv.getToken();
    if (accessToken) {
      headersOptions.Authorization = `Bearer ${accessToken}`;
    }

    const headers = new HttpHeaders(headersOptions);
    this.httpClient.get(`${path}`, { params, headers, responseType: 'arraybuffer' }).subscribe((res) => {
      this.downloadFile(res, 'application/pdf');
    });
  }

  downloadFile(data: any, type: string) {
    const blob = new Blob([data], { type });
    const url = window.URL.createObjectURL(blob);
    const pwa = window.open(url);
    if (!pwa || pwa.closed || typeof pwa.closed === 'undefined') {
      console.warn('Please disable your Pop-up blocker and try again.');
      alert('Please disable your Pop-up blocker and try again.');
    }
  }

  downloadExcelFile(data: any, type: string) {
    const pom = document.createElement('a');
    // const blob = new Blob(['\ufeff', data], { type });
    const blob = new Blob([data], { type });
    const url = window.URL.createObjectURL(blob);
    pom.href = url;
    pom.setAttribute('download', dayjs().format('YYYYMMDDHHmmss') + '.xlsx');
    pom.click();
  }

  protected paginate<T>(
    path: string,
    params: Params | Pagination,
    pagination?: Pagination
  ): Observable<PaginatedResponse<T>> {
    let request = {};
    if (params instanceof Pagination) {
      request = {
        skip: String(params.limit * params.page),
        take: String(params.limit),
      };
    } else {
      request = {
        ...params,
        skip: String(pagination.limit * pagination.page),
        take: String(pagination.limit),
      };
    }
    return this.__api<PaginatedResponse<T>>('get', path, request).pipe(
      mergeMap((res) => {
        res.limit = params.limit || pagination.limit;
        res.page = params.page || (pagination ? pagination.page || 0 : 0);
        return of(res);
      })
    );
  }
}
